import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import Pagination from '../../../components/common/Pagination';
import ContentLoader from '../../../components/common/ContentLoader';
import SEO from '../../../components/common/SEO';
import { toast } from 'react-toastify';
import { Modal, Button, Form, InputGroup, Table } from 'react-bootstrap';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import { buildVendorProfileUrl } from '../../../utils/vendorProfileUrl';
import { formatDateOnly, formatTimeOnly, getLocalDateString } from '../../../utils/dateUtils';

const VendorListingContent = () => {
    const defaultFilters = { location: '', mobile: '', company_name: '', email: '', plan_id: '', registration_date_from: '', registration_date_to: '' };
    const { type } = useParams();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);

    const prevTypeRef = useRef(type);

    // Pagination states
    const [page, setPage] = useState(() => {
        const saved = sessionStorage.getItem(`vendor_page_${type || 'all'}`);
        return saved ? parseInt(saved) : 1;
    });
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const [filters, setFilters] = useState(() => {
        const saved = sessionStorage.getItem(`vendor_filters_v2_${type || 'all'}`);
        return saved
            ? {
                ...defaultFilters,
                ...JSON.parse(saved),
                registration_date_from: '',
                registration_date_to: ''
            }
            : defaultFilters;
    });

    useEffect(() => {
        if (prevTypeRef.current !== type) {
            prevTypeRef.current = type;
            const savedPage = sessionStorage.getItem(`vendor_page_${type || 'all'}`);
            setPage(savedPage ? parseInt(savedPage) : 1);

            const savedFilters = sessionStorage.getItem(`vendor_filters_${type || 'all'}`);
            setFilters(savedFilters ? JSON.parse(savedFilters) : { location: '', mobile: '', company_name: '', email: '', plan_id: '' });
        }
    }, [type]);

    useEffect(() => {
        sessionStorage.setItem(`vendor_page_${type || 'all'}`, page.toString());
    }, [page, type]);

    useEffect(() => {
        const persistentFilters = {
            ...filters,
            registration_date_from: '',
            registration_date_to: ''
        };
        sessionStorage.setItem(`vendor_filters_v2_${type || 'all'}`, JSON.stringify(persistentFilters));
    }, [filters, type]);
    const [plans, setPlans] = useState([]);

    // Delete Vendor Modal state
    const [deleteModal, setDeleteModal] = useState({
        show: false,
        loading: false,
        deleting: false,
        vendor: null,
        counts: { holidays: 0, hotels: 0, cabs: 0, yatras: 0, doctors: 0, hospitals: 0, total: 0 }
    });

    const handleOpenDeleteModal = async (v) => {
        setDeleteModal({
            show: true,
            loading: true,
            deleting: false,
            vendor: v,
            counts: { holidays: 0, hotels: 0, cabs: 0, yatras: 0, doctors: 0, hospitals: 0, total: 0 }
        });

        try {
            const response = await superAdminApiClient.get(`/superadmin/vendors/${v.id}/delete-summary`);
            if (response.data && response.data.success) {
                setDeleteModal(prev => ({
                    ...prev,
                    loading: false,
                    counts: response.data.counts
                }));
            }
        } catch (error) {
            console.error('Error fetching delete summary:', error);
            setDeleteModal(prev => ({ ...prev, loading: false }));
        }
    };

    const handleConfirmDeleteVendor = async () => {
        if (!deleteModal.vendor) return;
        setDeleteModal(prev => ({ ...prev, deleting: true }));
        try {
            const response = await superAdminApiClient.delete(`/superadmin/vendors/${deleteModal.vendor.id}`);
            if (response.data && response.data.success) {
                toast.success('Vendor profile and all associated listings moved to Trash successfully.');
                setDeleteModal({ show: false, loading: false, deleting: false, vendor: null, counts: { holidays: 0, hotels: 0, cabs: 0, yatras: 0, doctors: 0, hospitals: 0, total: 0 } });
                fetchVendors();
            }
        } catch (error) {
            console.error('Error deleting vendor:', error);
            toast.error(error.response?.data?.message || 'Failed to move vendor to trash.');
            setDeleteModal(prev => ({ ...prev, deleting: false }));
        }
    };

    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [newVendor, setNewVendor] = useState({
        name: '', phone: '', email: '', address: '', city: '', categories: []
    });

    const handleCloseModal = () => {
        setShowAddModal(false);
        setNewVendor({ name: '', phone: '', email: '', address: '', city: '', categories: [] });
    };
    const handleShowModal = () => setShowAddModal(true);

    const handleNewVendorChange = (e) => {
        setNewVendor({ ...newVendor, [e.target.name]: e.target.value });
    };

    const handleCategoryToggle = (categoryName) => {
        setNewVendor(prev => {
            const current = Array.isArray(prev.categories) ? prev.categories : [];
            if (current.includes(categoryName)) {
                return { ...prev, categories: current.filter(c => c !== categoryName) };
            } else {
                return { ...prev, categories: [...current, categoryName] };
            }
        });
    };

    const fetchVendors = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                ...filters,
                ...(type ? { type } : {}),
                plan_id: filters.plan_id,
                page: page,
                limit: 20
            });
            const response = await superAdminApiClient.get(`/superadmin/vendors?${queryParams}`);
            setVendors(response.data.vendors);
            setTotalPages(response.data.total_pages);
            setTotalRecords(response.data.total_records);
        } catch (error) {
            console.error('Error fetching vendors', error);
        } finally {
            setLoading(false);
        }
    }, [filters, type, page]);

    const handleAddVendorSubmit = async (e) => {
        e.preventDefault();
        if (!newVendor.categories || newVendor.categories.length === 0) {
            toast.error('Please select at least one category.');
            return;
        }
        setSubmitting(true);
        try {
            const response = await superAdminApiClient.post('/superadmin/vendors', {
                ...newVendor,
                categories: newVendor.categories
            });
            if (response.data.success) {
                toast.success('Vendor added successfully and onboarding email sent.');
                setShowAddModal(false);
                setNewVendor({ name: '', phone: '', email: '', address: '', city: '', categories: [] });
                // trigger re-fetch
                setPage(1);
                await fetchVendors();
            }
        } catch (error) {
            console.error('Error adding vendor:', error);
            toast.error(error.response?.data?.message || 'Failed to add vendor');
        } finally {
            setSubmitting(false);
        }
    };

    const handleImpersonate = async (vendorId) => {
        try {
            const response = await superAdminApiClient.post(`/superadmin/vendors/${vendorId}/impersonate`);
            if (response.data.success) {
                window.open(response.data.redirectUrl, '_blank');
            }
        } catch (error) {
            console.error('Error impersonating vendor:', error);
            toast.error('Failed to access vendor profile.');
        }
    };

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await superAdminApiClient.get('/superadmin/plans');
                if (response.data.success) {
                    setPlans(response.data.plans);
                }
            } catch (error) {
                console.error('Error fetching plans', error);
            }
        };
        fetchPlans();
    }, []);

    useEffect(() => {
        // Debounce fetching if filters change fast
        const handler = setTimeout(() => {
            fetchVendors();
        }, 500);
        return () => clearTimeout(handler);
    }, [fetchVendors]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
        setPage(1); // Reset page on filter change
    };

    return (
        <React.Fragment>
            <SEO
                title="Vendor Listing | Mangoloops"
                description="List of all vendors on Mangoloops."
            />
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">{type ? type.charAt(0).toUpperCase() + type.slice(1) : 'All'} Vendors</h4>
                        {(type && type === "trial") && (
                            <div className="page-title-right">
                                <Button variant="success" onClick={handleShowModal}>
                                    <i className="bx bx-plus me-1"></i> Add New Vendor
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-6 mb-3 gy-2 gy-xl-0">
                <div className="col">
                    <input type="text" className="form-control" placeholder="Company Name" name="company_name" value={filters.company_name} onChange={handleFilterChange} />
                </div>
                <div className="col">
                    <input type="text" className="form-control" placeholder="Email" name="email" value={filters.email} onChange={handleFilterChange} />
                </div>
                <div className="col">
                    <input type="text" className="form-control" placeholder="Mobile" name="mobile" value={filters.mobile} onChange={handleFilterChange} />
                </div>
                <div className="col">
                    <input type="text" className="form-control" placeholder="Location" name="location" value={filters.location} onChange={handleFilterChange} />
                </div>
                <div className="col">
                    <Form.Select name="plan_id" value={filters.plan_id} onChange={handleFilterChange}>
                        <option value="">All Plans</option>
                        {plans.map(plan => (
                            <option key={plan.id} value={plan.id}>{plan.name}</option>
                        ))}
                    </Form.Select>
                </div>
                <div className="col">
                    <Flatpickr
                        className="form-control"
                        placeholder="Registration Date Range"
                        value={[filters.registration_date_from, filters.registration_date_to].filter(Boolean)}
                        options={{
                            mode: 'range',
                            dateFormat: 'Y-m-d'
                        }}
                        onChange={(dates) => {
                            if (dates.length === 2) {
                                setFilters(current => ({
                                    ...current,
                                    registration_date_from: getLocalDateString(dates[0]),
                                    registration_date_to: getLocalDateString(dates[1])
                                }));
                            } else if (dates.length === 0) {
                                setFilters(current => ({
                                    ...current,
                                    registration_date_from: '',
                                    registration_date_to: ''
                                }));
                            }
                            setPage(1);
                        }}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <ContentLoader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>SR No.</th>
                                                <th>Company Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Location</th>
                                                {!['free', 'trial'].includes(type) && <th>Active Subscription</th>}
                                                <th>Status</th>
                                                <th>Registered On</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {vendors.length > 0 ? vendors.map((v, index) => (
                                                <tr key={v.id}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <Link to={`/superadmin/vendors/detail/${v.id}`} className="">
                                                        {v.company_name && <div><i className="ri-building-line me-1"></i>{v.company_name}</div>}
                                                        {v.name && <div><i className="ri-user-line me-1"></i>{v.name}</div>}
                                                        </Link>
                                                    </td>
                                                    <td>{v.email ? v.email : '-'}</td>
                                                    <td>{v.phone}</td>
                                                    <td>{v.company_city ? v.company_city : '-'}</td>
                                                    {!['free', 'trial'].includes(type) && (
                                                        <td>{v.Subscriptions && v.Subscriptions[0]?.Plan?.name ? v.Subscriptions[0].Plan.name : <span className="text-muted">None</span>}</td>
                                                    )}
                                                    <td>{v.is_aadhaar_verified ? <span className="badge bg-success">Verified</span> : <span className="badge bg-warning">Pending</span>}</td>
                                                    <td>
                                                        {v.createdAt ? (
                                                            <>
                                                                <div>{formatDateOnly(v.createdAt)}</div>
                                                                <div className="text-muted fs-12">{formatTimeOnly(v.createdAt)}</div>
                                                            </>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        <a
                                                            href={buildVendorProfileUrl(v)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-info me-2"
                                                        >
                                                            <i className="la la-eye me-1"></i>
                                                        </a>
                                                        <button onClick={() => handleImpersonate(v.id)} className="btn btn-sm btn-secondary me-2" title="Impersonate">
                                                            <i className="bx bx-log-in-circle me-1"></i>
                                                        </button>
                                                        <button onClick={() => handleOpenDeleteModal(v)} className="btn btn-sm btn-danger" title="Move to Trash">
                                                            <i className="ri-delete-bin-line me-1"></i>Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={['free', 'trial'].includes(type) ? 8 : 9} className="text-center">No vendors found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing {vendors.length} of {totalRecords} vendors
                                        </div>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={(p) => setPage(p)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Vendor Modal */}
            <Modal show={showAddModal} onHide={handleCloseModal} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Add New Vendor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddVendorSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name / Company Name</Form.Label>
                            <Form.Control type="text" name="name" required value={newVendor.name} onChange={handleNewVendorChange} placeholder="Enter name" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control type="email" name="email" required value={newVendor.email} onChange={handleNewVendorChange} placeholder="Enter email" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-light text-muted fw-bold">+91</InputGroup.Text>
                                <Form.Control 
                                    type="text" 
                                    name="phone" 
                                    required 
                                    value={newVendor.phone} 
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        handleNewVendorChange({ target: { name: 'phone', value: val } });
                                    }} 
                                    placeholder="Enter 10-digit mobile number" 
                                    maxLength={10}
                                />
                            </InputGroup>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Categories (Select one or more)</Form.Label>
                            <div className="d-flex flex-wrap gap-2 p-2 border rounded bg-light">
                                {["Holidays", "Hotels", "Cab", "Tirth Yatra", "Medical Tourism"].map(cat => {
                                    const isSelected = (newVendor.categories || []).includes(cat);
                                    return (
                                        <div
                                            key={cat}
                                            onClick={() => handleCategoryToggle(cat)}
                                            className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            style={{ cursor: 'pointer', borderRadius: '20px', padding: '6px 14px', fontWeight: '500' }}
                                        >
                                            {isSelected ? '✓ ' : '+ '}{cat}
                                        </div>
                                    );
                                })}
                            </div>
                            {(!newVendor.categories || newVendor.categories.length === 0) && (
                                <Form.Text className="text-danger">
                                    Please click at least one category to select.
                                </Form.Text>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control type="text" name="address" required value={newVendor.address} onChange={handleNewVendorChange} placeholder="Enter full address" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Form.Control type="text" name="city" required value={newVendor.city} onChange={handleNewVendorChange} placeholder="Enter city" />
                        </Form.Group>

                        <div className="text-end mt-4">
                            <Button variant="light" onClick={handleCloseModal} className="me-2 text-secondary border">Cancel</Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? 'Adding...' : 'Add Vendor'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Move to Trash Confirmation Modal */}
            <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, loading: false, deleting: false, vendor: null, counts: { holidays: 0, hotels: 0, cabs: 0, yatras: 0, doctors: 0, hospitals: 0, total: 0 } })} backdrop="static">
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title className="text-white">
                        <i className="ri-delete-bin-line me-2"></i>Move Vendor to Trash
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteModal.vendor && (
                        <div>
                            <div className="alert alert-warning d-flex align-items-center mb-3">
                                <i className="ri-alert-line fs-24 me-2"></i>
                                <div>
                                    <strong>Move vendor profile to Trash?</strong>
                                    <div className="small">This vendor profile and all associated listings will be moved to Trash. They can be restored within 30 days.</div>
                                </div>
                            </div>

                            <div className="card border bg-light mb-3">
                                <div className="card-body p-3">
                                    <div className="mb-1"><strong>Vendor Name:</strong> {deleteModal.vendor.name || '-'}</div>
                                    <div className="mb-1"><strong>Company Name:</strong> {deleteModal.vendor.company_name || '-'}</div>
                                    <div><strong>Email:</strong> {deleteModal.vendor.email || '-'}</div>
                                </div>
                            </div>

                            <h6 className="fw-semibold mb-2">Package-wise Listing Summary:</h6>
                            {deleteModal.loading ? (
                                <div className="text-center py-3"><ContentLoader /></div>
                            ) : (
                                <Table className="table table-bordered table-sm align-middle text-center mb-3">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Package</th>
                                            <th>Listings</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr><td>Holidays</td><td>{deleteModal.counts.holidays}</td></tr>
                                        <tr><td>Hotels</td><td>{deleteModal.counts.hotels}</td></tr>
                                        <tr><td>Cabs</td><td>{deleteModal.counts.cabs}</td></tr>
                                        <tr><td>Yatras</td><td>{deleteModal.counts.yatras}</td></tr>
                                        <tr><td>Doctors</td><td>{deleteModal.counts.doctors}</td></tr>
                                        <tr><td>Hospitals</td><td>{deleteModal.counts.hospitals}</td></tr>
                                        <tr className="table-active fw-bold">
                                            <td>Total</td>
                                            <td className="text-danger fs-15">{deleteModal.counts.total}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteModal({ show: false, loading: false, deleting: false, vendor: null, counts: { holidays: 0, hotels: 0, cabs: 0, yatras: 0, doctors: 0, hospitals: 0, total: 0 } })}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDeleteVendor} disabled={deleteModal.deleting || deleteModal.loading}>
                        {deleteModal.deleting ? 'Moving to Trash...' : 'Move to Trash'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </React.Fragment>
    );
};

const VendorListing = () => {
    const { type } = useParams();

    // Remount the listing when its type changes so Paid, Free, and Trial do not
    // share in-memory filters while React reuses this route component.
    return <VendorListingContent key={type || 'all'} />;
};

export default VendorListing;
